import * as THREE from 'https://esm.sh/three@0.183.0';
import { GLTFLoader } from 'https://esm.sh/three@0.183.0/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'https://esm.sh/three@0.183.0/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'https://esm.sh/three@0.183.0/examples/jsm/environments/RoomEnvironment.js';

const BASELINE_HAND_INCHES = 7.480315;
const INCHES_TO_METERS = 0.0254;
// Permit sub-millimeter bevel and export-envelope differences without allowing
// one nominal product size to masquerade as another.
const DIMENSION_TOLERANCE = 0.01;
const PROFILE_KEY = 'inspire-hardware-hand-profile-v2';
const HAND_CENTER_X = 0.035;
const HARDWARE_HAND_GAP = 0.002;
const FINGERTIP_CENTER_Y = -0.045;

const getControls = (comparison) => ({
  length: comparison.querySelector('[data-hand-length]'),
  lengthOutput: comparison.querySelector('[data-hand-length-output]'),
  unit: comparison.querySelector('[data-hand-unit]'),
  shape: comparison.querySelector('[data-hand-shape]'),
  side: comparison.querySelector('[data-hand-side]'),
});

const getProfileKey = (comparison) => {
  const customerId = comparison.querySelector('[data-save-hand]')?.dataset.customerId;
  return customerId ? `${PROFILE_KEY}:customer:${customerId}` : PROFILE_KEY;
};

const persistProfile = (comparison) => {
  const controls = getControls(comparison);
  const profile = {
    length: controls.length.value,
    unit: controls.unit.value,
    shape: controls.shape.value,
    side: controls.side.value,
    skinColor: comparison.handScene?.skinColor || comparison.dataset.savedSkinColor || '#d99b73',
  };
  try {
    localStorage.setItem(getProfileKey(comparison), JSON.stringify(profile));
  } catch (error) {
    console.warn('Hand profile could not be saved.', error);
  }
};

const readHandLengthInches = (controls) => {
  const value = Number(controls.length.value);
  return controls.unit.value === 'cm' ? value / 2.54 : value;
};

const setSelectedTone = (comparison, button) => {
  comparison.querySelectorAll('[data-skin-tone]').forEach((tone) => tone.classList.toggle('is-selected', tone === button));
};

const centerModel = (model) => {
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  model.position.sub(center);
  return box.getSize(new THREE.Vector3());
};

const prepareModel = (model) => {
  model.traverse((node) => {
    if (!node.isMesh) return;
    node.castShadow = true;
    node.receiveShadow = false;
    if (Array.isArray(node.material)) node.material = node.material.map((material) => material.clone());
    else if (node.material) node.material = node.material.clone();
  });
};

const longestDimension = (size) => Math.max(size.x, size.y, size.z);

const assertTrueScale = (comparison, handSize, hardwareSize) => {
  const expectedHandMeters = BASELINE_HAND_INCHES * INCHES_TO_METERS;
  const expectedHardwareMeters = Number(comparison.dataset.productLengthInches) * INCHES_TO_METERS;
  const actualHandMeters = longestDimension(handSize);
  const actualHardwareMeters = longestDimension(hardwareSize);
  const withinTolerance = (actual, expected) => Math.abs(actual - expected) <= expected * DIMENSION_TOLERANCE;

  if (!withinTolerance(actualHandMeters, expectedHandMeters)) {
    throw new Error(`Reference hand is not true scale: ${actualHandMeters}m != ${expectedHandMeters}m.`);
  }
  if (!Number.isFinite(expectedHardwareMeters) || !withinTolerance(actualHardwareMeters, expectedHardwareMeters)) {
    throw new Error(`Variant hardware is not true scale: ${actualHardwareMeters}m != ${expectedHardwareMeters}m.`);
  }

  comparison.dataset.scaleValidation = 'PASS';
  comparison.dataset.handLengthMeters = actualHandMeters.toFixed(6);
  comparison.dataset.hardwareLengthMeters = actualHardwareMeters.toFixed(6);
};

const resolveActiveVariantHardware = (comparison) => {
  const productInfo = comparison.closest('product-info');
  const variantModel = productInfo?.querySelector('.variant-model');
  const viewer = productInfo?.querySelector('.variant-model__viewer');
  const expectedSku = comparison.dataset.variantSku;
  const activeSku = variantModel?.dataset.variantSku;
  if (!viewer?.src) throw new Error(`No active solo 3D viewer exists for ${expectedSku || 'this variant'}.`);
  if (!expectedSku || activeSku !== expectedSku) throw new Error(`Hand/solo SKU mismatch: ${expectedSku || 'missing'} != ${activeSku || 'missing'}.`);
  comparison.dataset.resolvedHardwareSrc = viewer.src;
  return viewer.src;
};

class HandComparisonScene {
  constructor(comparison) {
    this.comparison = comparison;
    this.canvas = comparison.querySelector('[data-hand-scene-canvas]');
    this.stage = this.canvas.parentElement;
    this.loading = comparison.querySelector('[data-hand-scene-status]');
    this.controls = getControls(comparison);
    this.skinColor = '#d99b73';
    this.loader = new GLTFLoader();
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf8f7f4);
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.01, 10);
    this.camera.position.set(-0.015, 0, 0.34);

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.8;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    const environmentGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = environmentGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
    environmentGenerator.dispose();

    this.orbit = new OrbitControls(this.camera, this.canvas);
    this.orbit.enableDamping = false;
    this.orbit.enablePan = false;
    this.orbit.minDistance = 0.22;
    this.orbit.maxDistance = 0.75;
    this.orbit.target.set(-0.015, 0, 0);
    this.orbit.addEventListener('change', () => this.render());

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xcbb98f, 1));

    const backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(1.2, 1.2),
      new THREE.ShadowMaterial({ color: 0x3a302a, opacity: 0.13 }),
    );
    backdrop.position.z = -0.035;
    backdrop.receiveShadow = true;
    this.scene.add(backdrop);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.stage);
  }

  async load() {
    const hardwareSource = resolveActiveVariantHardware(this.comparison);
    const [handAsset, hardwareAsset] = await Promise.all([
      this.loader.loadAsync(this.stage.dataset.handSrc),
      this.loader.loadAsync(hardwareSource),
    ]);
    if (!this.comparison.isConnected) return;

    prepareModel(handAsset.scene);
    prepareModel(hardwareAsset.scene);
    // Verge's broad satin face needs the same usable studio illumination in both viewers.
    // Bind the existing environment explicitly so intensity affects hardware alone.
    if (/^Verg-P-No-SB[MG]-/.test(this.comparison.dataset.variantSku || '')) {
      hardwareAsset.scene.traverse((node) => {
        if (!node.isMesh) return;
        for (const material of [node.material].flat().filter(Boolean)) {
          material.envMap = this.scene.environment;
          material.envMapIntensity = 2;
          material.needsUpdate = true;
        }
      });
    }


    this.hand = new THREE.Group();
    this.hardware = new THREE.Group();
    this.hand.add(handAsset.scene);
    this.hardware.add(hardwareAsset.scene);
    this.handBaselineSize = centerModel(handAsset.scene);
    this.hardwareSize = centerModel(hardwareAsset.scene);
    assertTrueScale(this.comparison, this.handBaselineSize, this.hardwareSize);

    handAsset.scene.traverse((node) => {
      if (!node.isMesh) return;
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.filter(Boolean).forEach((material) => {
        if (material.name === 'IH Hand Skin' || !this.handMaterials?.length) {
          this.handMaterials ||= [];
          this.handMaterials.push(material);
        }
      });
    });

    this.scene.add(this.hardware, this.hand);
    this.loading.hidden = true;
    this.applyProfile();
    this.resize();
  }

  applyProfile() {
    if (!this.hand || !this.hardware) return;
    const lengthScale = readHandLengthInches(this.controls) / BASELINE_HAND_INCHES;
    const widthScale = Number(this.controls.shape.value);
    const sideScale = Number(this.controls.side.value);
    this.hand.scale.set(lengthScale * widthScale * sideScale, lengthScale, lengthScale);
    this.hand.rotation.y = Math.PI;

    this.hand.position.x = HAND_CENTER_X;
    this.applyHardwarePosition();
    this.hardware.scale.setScalar(1);
    this.controls.lengthOutput.textContent = `${Number(this.controls.length.value).toFixed(1)} ${this.controls.unit.value}`;
    this.setSkinColor(this.skinColor);
    this.fitCameraToScene();
  }

  applyHardwarePosition() {
    const handDepth = this.handBaselineSize.z * this.hand.scale.z;
    const hardwareDepth = this.hardwareSize.z * this.hardware.scale.z;
    // Keep every cabinet and appliance pull centered across the fingertips.
    // This family-neutral placement must persist when Shopify replaces the
    // section for a newly selected variant.
    const mountedCenterZ = (handDepth + hardwareDepth) / 2 + HARDWARE_HAND_GAP;
    this.hardware.position.set(HAND_CENTER_X, FINGERTIP_CENTER_Y, mountedCenterZ);
    this.hardware.rotation.set(0, 0, 0);
  }

  setSkinColor(color) {
    this.skinColor = color;
    this.handMaterials?.forEach((material) => {
      material.color.set(color);
      material.needsUpdate = true;
    });
    this.render();
  }

  fitCameraToScene() {
    if (!this.hand || !this.hardware) return;
    this.hand.updateMatrixWorld(true);
    this.hardware.updateMatrixWorld(true);

    const bounds = new THREE.Box3();
    bounds.expandByObject(this.hand);
    bounds.expandByObject(this.hardware);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(this.camera.aspect, 0.01));
    const heightDistance = size.y / (2 * Math.tan(verticalFov / 2));
    const widthDistance = size.x / (2 * Math.tan(horizontalFov / 2));
    const framingDistance = (Math.max(heightDistance, widthDistance) + size.z / 2) * 1.18;
    this.framingSize = size.clone();
    this.framingCenter = center.clone();
    this.framingDistance = framingDistance;

    this.camera.near = Math.max(0.001, framingDistance / 100);
    this.camera.far = Math.max(10, framingDistance * 20);
    this.camera.position.set(center.x, center.y, center.z + framingDistance);
    this.camera.updateProjectionMatrix();
    this.orbit.target.copy(center);
    this.orbit.minDistance = Math.max(0.12, framingDistance * 0.2);
    this.orbit.maxDistance = Math.max(1.2, framingDistance * 4);
    this.orbit.update();
    this.render();
  }

  resize() {
    const width = Math.max(1, this.stage.clientWidth);
    const height = Math.max(1, this.stage.clientHeight);
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    if (this.hand && this.hardware) this.fitCameraToScene();
    else this.render();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }
}

const loadSavedProfile = (comparison) => {
  try {
    const profile = JSON.parse(localStorage.getItem(getProfileKey(comparison)));
    if (!profile) return;
    const controls = getControls(comparison);
    controls.unit.value = profile.unit || 'in';
    controls.unit.dataset.previousUnit = controls.unit.value;
    controls.length.min = controls.unit.value === 'cm' ? '14' : '5.5';
    controls.length.max = controls.unit.value === 'cm' ? '24' : '9.5';
    controls.length.value = profile.length || (controls.unit.value === 'cm' ? '19.0' : '7.5');
    controls.shape.value = profile.shape || '1';
    controls.side.value = profile.side || '1';
    comparison.dataset.savedSkinColor = profile.skinColor || '#d99b73';
  } catch (error) {
    console.warn('Saved hand profile could not be loaded.', error);
  }
};

const initializeComparison = async (comparison) => {
  if (comparison.dataset.initialized === 'true') return;
  comparison.dataset.initialized = 'true';
  comparison.querySelector('[data-hand-unit]').dataset.previousUnit = 'in';
  loadSavedProfile(comparison);
  const scene = new HandComparisonScene(comparison);
  comparison.handScene = scene;
  scene.skinColor = comparison.dataset.savedSkinColor || '#d99b73';
  try {
    await scene.load();
    const matchingTone = comparison.querySelector(`[data-skin-tone="${scene.skinColor}"]`);
    setSelectedTone(comparison, matchingTone);
  } catch (error) {
    comparison.querySelector('[data-hand-scene-status]').textContent = 'The hand comparison could not load. Please refresh and try again.';
    console.error('Hand comparison failed to load.', error);
  }
};

const initializeAll = () => document.querySelectorAll('[data-hand-comparison]').forEach(initializeComparison);

document.addEventListener('input', (event) => {
  const comparison = event.target.closest('[data-hand-comparison]');
  if (!comparison) return;
  if (event.target.matches('[data-hand-length]')) {
    comparison.handScene?.applyProfile();
    persistProfile(comparison);
  }
  if (event.target.matches('[data-skin-color]')) {
    setSelectedTone(comparison, null);
    comparison.handScene?.setSkinColor(event.target.value);
    persistProfile(comparison);
  }
});

document.addEventListener('change', (event) => {
  const comparison = event.target.closest('[data-hand-comparison]');
  if (!comparison) return;
  const controls = getControls(comparison);
  if (event.target.matches('[data-hand-unit]')) {
    const previousUnit = controls.unit.dataset.previousUnit || 'in';
    let value = Number(controls.length.value);
    if (previousUnit !== controls.unit.value) value = controls.unit.value === 'cm' ? value * 2.54 : value / 2.54;
    controls.length.min = controls.unit.value === 'cm' ? '14' : '5.5';
    controls.length.max = controls.unit.value === 'cm' ? '24' : '9.5';
    controls.length.value = value.toFixed(1);
    controls.unit.dataset.previousUnit = controls.unit.value;
  }
  if (event.target.matches('[data-hand-unit], [data-hand-shape], [data-hand-side]')) {
    comparison.handScene?.applyProfile();
    persistProfile(comparison);
  }
});

document.addEventListener('click', (event) => {
  const openSettings = event.target.closest('[data-open-hand-settings]');
  if (openSettings) {
    const comparison = openSettings.closest('[data-hand-comparison]');
    comparison.querySelector('[data-hand-settings-dialog]')?.showModal();
    return;
  }

  const tone = event.target.closest('[data-skin-tone]');
  if (tone) {
    const comparison = tone.closest('[data-hand-comparison]');
    setSelectedTone(comparison, tone);
    comparison.handScene?.setSkinColor(tone.dataset.skinTone);
    comparison.querySelector('[data-skin-color]').value = tone.dataset.skinTone;
    persistProfile(comparison);
    return;
  }

  const save = event.target.closest('[data-save-hand]');
  if (save) {
    const comparison = save.closest('[data-hand-comparison]');
    persistProfile(comparison);
    comparison.querySelector('[data-save-status]').textContent = 'Saved for your signed-in profile on this device.';
    return;
  }

  const toggle = event.target.closest('.variant-model__comparison-toggle');
  if (!toggle) return;
  const comparison = toggle.closest('[data-hand-comparison]');
  const body = comparison.querySelector('.variant-model__comparison-body');
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  toggle.textContent = expanded ? 'Show comparison' : 'Hide comparison';
  body.hidden = expanded;
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
else initializeAll();
document.addEventListener('product-info:loaded', initializeAll);
document.addEventListener('shopify:section:load', initializeAll);
new MutationObserver(initializeAll).observe(document.documentElement, { childList: true, subtree: true });
